import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form as FormikForm } from "formik";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  FormikTextarea,
  Icon,
  PageHeader,
  Pagination,
  Skeleton,
  styles as ui,
} from "stories/Admin";
import NotificationList from "./NotificationList/NotificationList";
import styles from "./MawaqeetPushPage.module.scss";

interface PushNotificationForm {
  title: string;
  imgurl: string;
  body: string;
  uid: string;
}

interface Notification {
  id: number;
  title: string;
  body: string;
  date: string;
}

const MAX_CHAR_LIMIT = 255;
const NOTIFICATIONS_PER_PAGE = 10;
const MAWAQEET_ORGNR = "prayertable_id_2";

const validationSchema = Yup.object().shape({
  body: Yup.string()
    .required("Melding er påkrevd")
    .max(MAX_CHAR_LIMIT, `Melding kan ikke overstige ${MAX_CHAR_LIMIT} tegn`),
});

const MawaqeetPushPage: React.FC = () => {
  const { data, isLoading, error, execute } = useFetch<any>();
  const {
    data: notificationsData,
    isLoading: isNotificationsLoading,
    execute: fetchNotifications,
  } = useFetch<any>();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchNotifications(
        `/api/v1/notification/${MAWAQEET_ORGNR}`,
        "GET",
      );
      if (response) {
        const sortedNotifications = response.sort(
          (a: Notification, b: Notification) =>
            new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setNotifications(sortedNotifications);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (
    values: PushNotificationForm,
    { resetForm }: any,
  ) => {
    const payload = {
      orgnr: "",
      title: "Mawaqeet",
      imgurl: "",
      body: values.body,
    };

    await execute(
      `/api/v1/notification/send-notification/prayertable-id-2`,
      "POST",
      payload,
    );

    resetForm();
  };

  const indexOfLastNotification = currentPage * NOTIFICATIONS_PER_PAGE;
  const indexOfFirstNotification =
    indexOfLastNotification - NOTIFICATIONS_PER_PAGE;
  const currentNotifications = notifications.slice(
    indexOfFirstNotification,
    indexOfLastNotification,
  );
  const totalPages = Math.ceil(notifications.length / NOTIFICATIONS_PER_PAGE);

  return (
    <>
      <PageHeader
        title="Push-meldinger"
        subtitle="Sendes til alle som bruker Mawaqeet-tabellen i iBønn."
      />

      <Formik
        initialValues={{
          title: "",
          imgurl: "",
          body: "",
          uid: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, values, setFieldValue }) => (
          <div className={styles.top}>
            <Card title="Ny melding">
              <FormikForm className={styles.form} noValidate>
                <FormikTextarea
                  name="body"
                  id="formBody"
                  label="Melding"
                  required
                  rows={3}
                  placeholder="Skriv meldingen her …"
                  maxLength={MAX_CHAR_LIMIT}
                  maxChars={MAX_CHAR_LIMIT}
                  onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                    const value = e.currentTarget.value.slice(
                      0,
                      MAX_CHAR_LIMIT,
                    );
                    setFieldValue("body", value);
                  }}
                />
                <div className={styles.sendRow}>
                  <span className={ui.help}>
                    Tittel: <b>Mawaqeet</b> (fast)
                  </span>
                  <Button
                    type="submit"
                    loading={isLoading}
                    disabled={!(isValid && dirty)}
                    icon={<SendIcon />}
                  >
                    Send melding
                  </Button>
                </div>

                {error && (
                  <Alert tone="error">Kunne ikke sende: {error.text}</Alert>
                )}
                {data && <Alert tone="ok">Push-meldingen er sendt.</Alert>}
              </FormikForm>
            </Card>

            <section className={styles.preview} aria-label="Forhåndsvisning">
              <span className={styles.previewLabel}>Forhåndsvisning</span>
              <div className={styles.previewNote}>
                <span className={styles.previewIcon} aria-hidden="true">
                  <Icon name="moon" size={18} strokeWidth={2.2} />
                </span>
                <span className={styles.previewText}>
                  <span className={styles.previewTop}>
                    <b>Mawaqeet</b>
                    <span>nå</span>
                  </span>
                  <span>{values.body || "Meldingen vises her."}</span>
                </span>
              </div>
              <span className={styles.previewHelp}>
                Slik ser varselet ut på mobilen.
              </span>
            </section>
          </div>
        )}
      </Formik>

      <Card
        title="Sendte meldinger"
        actions={<span className={ui.help}>10 per side · nyeste først</span>}
      >
        {isNotificationsLoading ? (
          <div className={styles.skeletonRows}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} height={22} />
            ))}
          </div>
        ) : error ? (
          <Alert tone="error">Kunne ikke hente meldingene.</Alert>
        ) : notifications.length > 0 ? (
          <>
            <NotificationList notifications={currentNotifications} />
            <Pagination
              page={currentPage}
              pageCount={totalPages}
              onChange={setCurrentPage}
            />
          </>
        ) : (
          <EmptyState icon="bell" title="Ingen meldinger ennå">
            Meldinger du sender, vises her.
          </EmptyState>
        )}
      </Card>
    </>
  );
};

const SendIcon: React.FC = () => (
  <svg
    className={ui.icon}
    viewBox="0 0 24 24"
    width={16}
    height={16}
    strokeWidth={2}
    aria-hidden="true"
  >
    <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
  </svg>
);

export default MawaqeetPushPage;
